import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Slider,
  Grid,
  Button,
  Chip,
  Divider,
  Alert,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Paper,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  Rocket as RocketIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon,
  EmojiEvents as EmojiEventsIcon,
  Speed as SpeedIcon,
  LocalOffer as LocalOfferIcon,
} from "@mui/icons-material";
import axios from 'axios';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import { toast } from 'react-toastify';

// Token handling utilities
const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';

function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

const PropertyBoost = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  
  const [commissionRate, setCommissionRate] = useState(15);
  const [isBoostActive, setIsBoostActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [currentRate, setCurrentRate] = useState(15);
  const [showSuccess, setShowSuccess] = useState(false);

  // Load current settings on component mount and when hotel changes
  useEffect(() => {
    loadCurrentSettings();
  }, []);

  // Listen for hotel selection changes
  useEffect(() => {
    const handleStorageChange = () => {
      loadCurrentSettings();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events when hotel selection changes
    const handleHotelChange = () => {
      loadCurrentSettings();
    };
    
    window.addEventListener('hotelSelectionChanged', handleHotelChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('hotelSelectionChanged', handleHotelChange);
    };
  }, []);

  const loadCurrentSettings = async () => {
    setLoadingSettings(true);
    try {
      const hotelId = localStorage.getItem('selectedHotelId');
      
      if (!hotelId) {
        console.log('No hotel selected');
        setLoadingSettings(false);
        return;
      }

      const payload = {
        hotel_id: hotelId
      };

      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getCommission',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.status === true && response.data.commission) {
        const currentCommission = parseInt(response.data.commission);
        setCurrentRate(currentCommission);
        setCommissionRate(currentCommission);
        setIsBoostActive(currentCommission > 15);
        // console.log('Current commission loaded:', currentCommission);
      } else {
        // If no commission is set, use default 15%
        setCurrentRate(15);
        setCommissionRate(15);
        setIsBoostActive(false);
        console.log('No commission found, using default 15%');
      }
    } catch (error) {
      console.error('Error loading commission settings:', error);
      // Use default values on error
      setCurrentRate(15);
      setCommissionRate(15);
      setIsBoostActive(false);
      
      if (error.response?.data?.message) {
        // toast.error(`Failed to load commission: ${error.response.data.message}`); // Disabled
      } else {
        // toast.error('Failed to load current commission settings'); // Disabled
      }
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleSliderChange = (event, newValue) => {
    setCommissionRate(newValue);
  };

  const handleSaveBoost = async () => {
    setLoading(true);
    try {
      const hotelId = localStorage.getItem('selectedHotelId');
      
      if (!hotelId) {
        // toast.error('Hotel ID not found. Please select a hotel first.'); // Disabled
        setLoading(false);
        return;
      }

      const payload = {
        hotel_id: hotelId,
        commission: commissionRate
      };

      // toast.info('Updating commission rate...'); // Disabled

      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/setCommission',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.status === true) {
        setCurrentRate(commissionRate);
        setIsBoostActive(commissionRate > 15);
        setShowSuccess(true);
        // toast.success(`🚀 Property boost updated! Commission set to ${commissionRate}%`); // Disabled
        setTimeout(() => setShowSuccess(false), 3000);
        
        // Reload the current settings to confirm the update
        setTimeout(() => {
          loadCurrentSettings();
        }, 1000);
      } else {
        // toast.error(response.data.message || 'Failed to update commission rate'); // Disabled
      }

    } catch (error) {
      console.error('Error saving commission settings:', error);
      
      if (error.response?.data?.message) {
        // toast.error(`Failed to update commission: ${error.response.data.message}`); // Disabled
      } else {
        // toast.error('Failed to save commission settings. Please try again.'); // Disabled
      }
    } finally {
      setLoading(false);
    }
  };

  const getBoostLevel = () => {
    if (commissionRate <= 18) return { level: "Standard", color: "default", icon: <StarIcon /> };
    if (commissionRate <= 25) return { level: "Enhanced", color: "info", icon: <VisibilityIcon /> };
    if (commissionRate <= 32) return { level: "Premium", color: "warning", icon: <TrendingUpIcon /> };
    if (commissionRate <= 37) return { level: "Elite", color: "secondary", icon: <RocketIcon /> };
    return { level: "Maximum", color: "error", icon: <EmojiEventsIcon /> };
  };

  const getVisibilityIncrease = () => {
    const baseIncrease = Math.floor((commissionRate - 15) * 4.8);
    return Math.min(baseIncrease, 120);
  };

  const getExpectedBookingIncrease = () => {
    const baseIncrease = Math.floor((commissionRate - 15) * 4);
    return Math.min(baseIncrease, 100);
  };

  const boostLevel = getBoostLevel();
  const visibilityIncrease = getVisibilityIncrease();
  const bookingIncrease = getExpectedBookingIncrease();

  // Animation variants
  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary" 
          sx={{ display: "flex", alignItems: "center" }}>
          <RocketIcon sx={{ mr: 1 }} /> Property Boost
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Increase your property's visibility and bookings by adjusting your commission rate
        </Typography>
      </motion.div>

      {showSuccess && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            🚀 Property boost activated! Your property will now receive enhanced visibility.
          </Alert>
        </motion.div>
      )}

      {loadingSettings ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Loading commission settings...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we fetch your current boost settings
            </Typography>
          </Box>
        </Box>
      ) : (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerAnimation}
        >
          <Grid container spacing={3}>
          {/* Current Status Cards */}
          <Grid item xs={12}>
            <motion.div variants={itemAnimation}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={6} md={3}>
                  <Card elevation={2} sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white'
                  }}>
                    <CardContent sx={{ 
                      textAlign: 'center',
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 1, sm: 2 }
                    }}>
                      <CurrencyRupeeIcon sx={{ 
                        fontSize: { xs: 28, sm: 40 }, 
                        mb: { xs: 0.5, sm: 1 } 
                      }} />
                      <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                        {currentRate}%
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        opacity: 0.9,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}>
                        Current Commission
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <Card elevation={2}>
                    <CardContent sx={{ 
                      textAlign: 'center',
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 1, sm: 2 }
                    }}>
                      <VisibilityIcon color="info" sx={{ 
                        fontSize: { xs: 28, sm: 40 }, 
                        mb: { xs: 0.5, sm: 1 } 
                      }} />
                      <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="info.main">
                        +{visibilityIncrease}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}>
                        Extra Visibility Boost
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <Card elevation={2}>
                    <CardContent sx={{ 
                      textAlign: 'center',
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 1, sm: 2 }
                    }}>
                      <TrendingUpIcon color="success" sx={{ 
                        fontSize: { xs: 28, sm: 40 }, 
                        mb: { xs: 0.5, sm: 1 } 
                      }} />
                      <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="success.main">
                        +{bookingIncrease}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}>
                        Extra Expected Bookings
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <Card elevation={2}>
                    <CardContent sx={{ 
                      textAlign: 'center',
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 1, sm: 2 }
                    }}>
                      <Chip 
                        icon={boostLevel.icon}
                        label={boostLevel.level}
                        color={boostLevel.color}
                        size={isMobile ? "medium" : "large"}
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '1rem' }, 
                          py: { xs: 1.5, sm: 2 }, 
                          px: { xs: 0.5, sm: 1 }, 
                          mb: { xs: 0.5, sm: 1 } 
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}>
                        Boost Level
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </motion.div>
          </Grid>

          {/* Main Boost Control */}
          <Grid item xs={12} md={8}>
            <motion.div variants={itemAnimation}>
              <Card elevation={3} sx={{ 
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
                border: '1px solid rgba(102, 126, 234, 0.1)'
              }}>
                <CardHeader 
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SpeedIcon color="primary" />
                      Commission Boost Controller
                    </Box>
                  }
                  subheader="Adjust your commission rate to increase property visibility"
                />
                <Divider />
                <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 2 
                    }}>
                      <Typography variant="h6" color="text.primary">
                        Commission Rate
                      </Typography>
                      <Typography variant="h3" fontWeight="bold" color="primary.main">
                        {commissionRate}%
                      </Typography>
                    </Box>
                    
                    <Box sx={{ px: 2, mb: 3 }}>
                      <Slider
                        value={commissionRate}
                        onChange={handleSliderChange}
                        min={15}
                        max={40}
                        step={1}
                        marks={[
                          { value: 15, label: '15%' },
                          { value: 20, label: '20%' },
                          { value: 25, label: '25%' },
                          { value: 30, label: '30%' },
                          { value: 35, label: '35%' },
                          { value: 40, label: '40%' }
                        ]}
                        sx={{
                          height: 8,
                          '& .MuiSlider-track': {
                            background: 'linear-gradient(90deg, #4caf50 0%, #ff9800 50%, #f44336 100%)',
                            border: 'none',
                          },
                          '& .MuiSlider-thumb': {
                            height: 24,
                            width: 24,
                            backgroundColor: '#fff',
                            border: '3px solid currentColor',
                            '&:hover, &.Mui-focusVisible': {
                              boxShadow: `0px 0px 0px 8px rgba(102, 126, 234, 0.16)`,
                            },
                          },
                          '& .MuiSlider-markLabel': {
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: 'text.secondary'
                          }
                        }}
                      />
                    </Box>

                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 3,
                      p: 2,
                      bgcolor: 'rgba(102, 126, 234, 0.05)',
                      borderRadius: 2,
                      border: '1px solid rgba(102, 126, 234, 0.1)'
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        Minimum: 15% (Standard rate)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Maximum: 40% (Premium visibility)
                      </Typography>
                    </Box>

                    {/* Impact Indicators */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <VisibilityIcon color="info" sx={{ mr: 1 }} />
                            <Typography variant="subtitle2" color="info.main" fontWeight="600">
                              Visibility Impact
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min((visibilityIncrease / 120) * 100, 100)}
                            sx={{ 
                              mb: 1,
                              height: 8,
                              borderRadius: 4,
                              bgcolor: 'info.100',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: 'info.main',
                                borderRadius: 4
                              }
                            }}
                          />
                          <Typography variant="body2" color="info.dark">
                            +{visibilityIncrease}% more visibility in search results
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                            <Typography variant="subtitle2" color="success.main" fontWeight="600">
                              Booking Potential
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min((bookingIncrease / 100) * 100, 100)}
                            sx={{ 
                              mb: 1,
                              height: 8,
                              borderRadius: 4,
                              bgcolor: 'success.100',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: 'success.main',
                                borderRadius: 4
                              }
                            }}
                          />
                          <Typography variant="body2" color="success.dark">
                            +{bookingIncrease}% potential booking increase
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => setCommissionRate(15)}
                        sx={{ 
                          borderRadius: 2,
                          px: 3,
                          textTransform: 'none',
                          fontWeight: 600
                        }}
                      >
                        Reset to 15%
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleSaveBoost}
                        disabled={loading || commissionRate === currentRate}
                        startIcon={loading ? <TimelineIcon /> : <RocketIcon />}
                        sx={{ 
                          borderRadius: 2,
                          px: 4,
                          textTransform: 'none',
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                          }
                        }}
                      >
                        {loading ? 'Updating...' : 'Apply Boost'}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Marketing Strategy & Benefits */}
          <Grid item xs={12} md={4}>
            <motion.div variants={itemAnimation}>
              <Card elevation={2} sx={{ height: 'fit-content' }}>
                <CardHeader 
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalOfferIcon color="secondary" />
                      Marketing Strategy
                    </Box>
                  }
                />
                <Divider />
                <CardContent>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="600">
                      How Property Boost Works:
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • Higher commission = Better search ranking
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • Increased visibility to potential guests
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • Priority placement in search results
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • Enhanced property highlighting
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="secondary" gutterBottom fontWeight="600">
                      Boost Level Benefits:
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      {[
                        { range: "15-18%", level: "Standard", benefit: "Basic visibility" },
                        { range: "19-25%", level: "Enhanced", benefit: "Improved search ranking" },
                        { range: "26-32%", level: "Premium", benefit: "Priority placement" },
                        { range: "33-37%", level: "Elite", benefit: "Featured property status" },
                        { range: "38-40%", level: "Maximum", benefit: "Top-tier visibility" }
                      ].map((item, index) => (
                        <Box key={index} sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.5,
                          bgcolor: commissionRate >= parseInt(item.range.split('-')[0]) && 
                                   commissionRate <= parseInt(item.range.split('-')[1]) ? 
                                   'primary.50' : 'transparent',
                          borderRadius: 1,
                          px: 1
                        }}>
                          <Typography variant="caption" fontWeight="600">
                            {item.range}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.benefit}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Paper elevation={0} sx={{ 
                    p: 2, 
                    bgcolor: 'warning.50',
                    border: '1px solid',
                    borderColor: 'warning.200'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <InfoIcon color="warning" sx={{ fontSize: 20, mt: 0.5 }} />
                      <Box>
                        <Typography variant="subtitle2" color="warning.main" fontWeight="600">
                          Pro Tip
                        </Typography>
                        <Typography variant="body2" color="warning.dark">
                          Monitor your booking performance and adjust commission rates based on demand patterns for optimal results.
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          </Grid>
        </motion.div>
      )}
    </Box>
  );
};

export default PropertyBoost;
